import { del, get, keys, set } from 'idb-keyval';
import { sameSessionUser } from '@/features/auth/session-boundary';
import { logger } from '@/lib/logging';
import {
  EntryWorkspaceDraftSchema,
  type PersistedEntryWorkspaceDraft,
} from './entry-workspace-draft-schema';
import {
  cancelAllScheduledWorkspaceDrafts,
  cancelScheduledWorkspaceDraft,
  markWorkspaceDraftDurability,
} from './workspace-draft-flush';

export type EntryWorkspaceDraftKey = (
  | { kind: 'entry'; entryId: string }
  | { kind: 'draft'; draftId: string }
) & { owner?: string | null };

export type EntryWorkspaceDraftMode = 'create' | 'update';

export type StagedCommentDraft = {
  clientId: string;
  body: string;
  createdAt: string; // ISO string (client clock)
};

export type StagedAttachmentAddDraft = {
  clientId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string; // ISO string (client clock)
};

export type StagedChecklistAddDraft = {
  clientId: string;
  text: string;
  isDone: boolean;
  createdAt: string; // ISO string (client clock)
};

export type StagedChecklistUpdateDraft = {
  id: string;
  text?: string;
  isDone?: boolean;
};

export type ChecklistOrderRef = { ref: 'id'; id: string } | { ref: 'clientId'; clientId: string };

export type EntryWorkspaceDraft = PersistedEntryWorkspaceDraft;
export type EntryWorkspaceDraftLoadFailureReason =
  | 'invalid_shape'
  | 'legacy_keywords'
  | 'legacy_responsible';

type StagedFileRecord = {
  blob: Blob;
  filename: string;
  mimeType: string;
  sizeBytes: number;
};

const DRAFT_PREFIX = 'shiftbook-entry-workspace:draft:';
const FILE_PREFIX = 'shiftbook-entry-workspace:file:';
const OWNED_PREFIX = 'shiftbook-entry-workspace:owned:';
let workspaceOwner: string | null = null;
let ownerGeneration = 0;
let operations: Promise<unknown> = Promise.resolve();

export function getWorkspaceOwner(): string | null {
  return workspaceOwner;
}

export function setWorkspaceOwner(owner: string | null): void {
  if (!sameSessionUser(workspaceOwner, owner)) {
    ownerGeneration += 1;
    cancelAllScheduledWorkspaceDrafts();
  }
  workspaceOwner = owner;
}

function serialize<T>(operation: () => Promise<T>): Promise<T> {
  const result = operations.then(operation);
  operations = result.catch(() => undefined);
  return result;
}

// Explicit null never falls back to the current identity. Captured callbacks from
// a prior identity cannot access storage while another identity is active.
function captureOwner(owner?: string | null) {
  const resolved = owner === undefined ? workspaceOwner : owner;
  const generation = ownerGeneration;
  return {
    owner: resolved,
    valid: () =>
      Boolean(resolved) &&
      sameSessionUser(resolved, workspaceOwner) &&
      generation === ownerGeneration,
  };
}

function ownedKey(owner: string, legacyKey: string): string {
  return `${OWNED_PREFIX}${JSON.stringify([owner, legacyKey])}`;
}

function wrapDraft(draft: unknown, savedAt: string | null = new Date().toISOString()) {
  return { draft, savedAt };
}

function unwrapDraft(value: unknown): { draft: unknown; savedAt: string | null } {
  if (value && typeof value === 'object' && 'draft' in value) {
    const savedAt = 'savedAt' in value && typeof value.savedAt === 'string' ? value.savedAt : null;
    return { draft: value.draft, savedAt };
  }
  return { draft: value, savedAt: null };
}

// Keep editing available when IndexedDB is unavailable or rejects a write.
// Track these records separately so recovery cannot discard them by navigating.
const memoryFallback = new Map<string, unknown>();
// A failed delete must not resurrect a discarded record, even if its previous
// owner logs back in. Keep it quarantined until a durable retry succeeds.
const pendingDeletions = new Set<string>();

function markPendingDeletion(key: string): void {
  pendingDeletions.add(key);
  memoryFallback.delete(key);
  markWorkspaceDraftDurability(key, false);
}

async function safeGet<T>(key: string): Promise<T | undefined> {
  if (pendingDeletions.has(key)) return undefined;
  if (memoryFallback.has(key)) return memoryFallback.get(key) as T;
  try {
    const value = await get<T>(key);
    return value ?? undefined;
  } catch {
    return memoryFallback.get(key) as T | undefined;
  }
}

async function safeSet<T>(key: string, value: T): Promise<boolean> {
  let durable = true;
  try {
    await set(key, value);
    memoryFallback.delete(key);
    markWorkspaceDraftDurability(key, true);
  } catch {
    durable = false;
    markWorkspaceDraftDurability(key, false);
    memoryFallback.set(key, value as unknown);
  }
  // A new write supersedes the deletion request, including a memory-only write.
  pendingDeletions.delete(key);
  return durable;
}

async function safeDel(key: string, strict = false): Promise<void> {
  markPendingDeletion(key);
  try {
    await del(key);
    pendingDeletions.delete(key);
    markWorkspaceDraftDurability(key, true);
  } catch (error) {
    logger.warn('Workspace deletion failed; quarantined records will be retried.', {
      event: 'shiftbook.workspace.delete_failed',
      error,
    });
    // Consent must report durable failure. Product saves and owner switches can
    // finish safely: the key is unreadable in this tab and remains queued for purge.
    if (strict) throw error;
  }
}

async function safeKeys(): Promise<string[]> {
  try {
    const stored = await keys();
    return [
      ...new Set([
        ...stored.filter((key): key is string => typeof key === 'string'),
        ...memoryFallback.keys(),
        ...pendingDeletions,
      ]),
    ];
  } catch {
    return [...new Set([...memoryFallback.keys(), ...pendingDeletions])];
  }
}

function toDraftStorageKey(key: EntryWorkspaceDraftKey): string {
  if (key.kind === 'entry') {
    return `${DRAFT_PREFIX}entry:${key.entryId}`;
  }
  return `${DRAFT_PREFIX}draft:${key.draftId}`;
}

function toFileStorageKey(clientId: string): string {
  return `${FILE_PREFIX}${clientId}`;
}

export async function loadEntryWorkspaceDraft(
  key: EntryWorkspaceDraftKey,
): Promise<EntryWorkspaceDraft | undefined> {
  const { draft } = await loadEntryWorkspaceDraftWithStatus(key);
  return draft;
}

function hasLegacyKeywordDraftShape(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') {
    return false;
  }

  const keywords = (raw as { keywords?: unknown }).keywords;
  if (!Array.isArray(keywords)) {
    return false;
  }

  return keywords.some((keyword) => typeof keyword === 'string');
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Pre-ENG-167 drafts persisted `responsibleUserId: string` (empty string =
// unassigned). Convert in place so operator work survives the deploy; only a
// malformed non-empty value is rejected, with its own named reason.
function migrateLegacyResponsibleDraftShape(
  raw: unknown,
): { ok: true; value: unknown } | { ok: false } {
  if (!raw || typeof raw !== 'object' || !('responsibleUserId' in raw)) {
    return { ok: true, value: raw };
  }

  const { responsibleUserId, ...rest } = raw as { responsibleUserId?: unknown } & Record<
    string,
    unknown
  >;
  if ('responsible' in rest) {
    return { ok: true, value: rest };
  }

  if (responsibleUserId === '' || responsibleUserId === null || responsibleUserId === undefined) {
    return { ok: true, value: { ...rest, responsible: null } };
  }

  if (typeof responsibleUserId === 'string' && UUID_PATTERN.test(responsibleUserId)) {
    return { ok: true, value: { ...rest, responsible: { kind: 'user', id: responsibleUserId } } };
  }

  return { ok: false };
}

export async function loadEntryWorkspaceDraftWithStatus(key: EntryWorkspaceDraftKey): Promise<{
  draft: EntryWorkspaceDraft | undefined;
  rejectionReason?: EntryWorkspaceDraftLoadFailureReason;
}> {
  const context = captureOwner(key.owner);
  return serialize(async () => {
    if (!context.valid() || !context.owner) return { draft: undefined };
    const result = await loadOwnedDraft(ownedKey(context.owner, toDraftStorageKey(key)));
    return context.valid() ? result : { draft: undefined };
  });
}

async function loadOwnedDraft(storageKey: string): Promise<{
  draft: EntryWorkspaceDraft | undefined;
  rejectionReason?: EntryWorkspaceDraftLoadFailureReason;
}> {
  const stored = unwrapDraft(await safeGet<unknown>(storageKey));
  const raw = stored.draft;
  if (raw === undefined || raw === null) {
    return { draft: undefined };
  }

  if (hasLegacyKeywordDraftShape(raw)) {
    await safeDel(storageKey);
    return {
      draft: undefined,
      rejectionReason: 'legacy_keywords',
    };
  }

  const migrated = migrateLegacyResponsibleDraftShape(raw);
  if (!migrated.ok) {
    await safeDel(storageKey);
    return {
      draft: undefined,
      rejectionReason: 'legacy_responsible',
    };
  }

  const parsed = EntryWorkspaceDraftSchema.safeParse(migrated.value);
  if (parsed.success && migrated.value !== raw) {
    // Persist the converted shape so the migration is one-time, not
    // re-derived (and re-losable) on every load.
    await safeSet(storageKey, wrapDraft(parsed.data, stored.savedAt));
  }
  if (!parsed.success) {
    // Reject incompatible persisted shapes instead of migrating them implicitly.
    await safeDel(storageKey);
    return {
      draft: undefined,
      rejectionReason: 'invalid_shape',
    };
  }

  return { draft: parsed.data };
}

export async function persistEntryWorkspaceDraft(
  key: EntryWorkspaceDraftKey,
  draft: PersistedEntryWorkspaceDraft,
): Promise<void> {
  const context = captureOwner(key.owner);
  await serialize(async () => {
    if (!context.valid() || !context.owner) return;
    await safeSet(ownedKey(context.owner, toDraftStorageKey(key)), wrapDraft(draft));
  });
}

export async function removeEntryWorkspaceDraft(key: EntryWorkspaceDraftKey): Promise<void> {
  const draftToken = key.kind === 'entry' ? `entry:${key.entryId}` : `draft:${key.draftId}`;
  cancelScheduledWorkspaceDraft(
    JSON.stringify([key.owner === undefined ? workspaceOwner : key.owner, draftToken]),
  );
  const context = captureOwner(key.owner);
  await serialize(async () => {
    if (!context.valid() || !context.owner) return;
    await safeDel(ownedKey(context.owner, toDraftStorageKey(key)));
  });
}

export async function persistStagedFile(params: {
  clientId: string;
  file: File;
  owner?: string | null;
}): Promise<StagedFileRecord> {
  const context = captureOwner(params.owner);
  const record: StagedFileRecord = {
    blob: params.file,
    filename: params.file.name,
    mimeType: params.file.type || 'application/octet-stream',
    sizeBytes: params.file.size,
  };
  await serialize(async () => {
    if (!context.valid() || !context.owner) return;
    await safeSet(ownedKey(context.owner, toFileStorageKey(params.clientId)), record);
  });
  return record;
}

export async function loadStagedFile(
  clientId: string,
  owner?: string | null,
): Promise<StagedFileRecord | undefined> {
  const context = captureOwner(owner);
  return serialize(async () => {
    if (!context.valid() || !context.owner) return undefined;
    const record = await safeGet<StagedFileRecord>(
      ownedKey(context.owner, toFileStorageKey(clientId)),
    );
    return context.valid() ? record : undefined;
  });
}

export async function removeStagedFile(clientId: string, owner?: string | null): Promise<void> {
  const context = captureOwner(owner);
  await serialize(async () => {
    if (!context.valid() || !context.owner) return;
    await safeDel(ownedKey(context.owner, toFileStorageKey(clientId)));
  });
}

export async function migrateEntryWorkspaceDraft(params: {
  from: EntryWorkspaceDraftKey;
  to: EntryWorkspaceDraftKey;
}): Promise<void> {
  const from = captureOwner(params.from.owner);
  const to = captureOwner(params.to.owner);
  await serialize(async () => {
    if (!from.valid() || !to.valid() || !from.owner || from.owner !== to.owner) return;
    const source = ownedKey(from.owner, toDraftStorageKey(params.from));
    const destination = ownedKey(from.owner, toDraftStorageKey(params.to));
    if (source === destination) return;
    const { draft } = await loadOwnedDraft(source);
    if (!draft || !from.valid() || !to.valid()) return;
    await safeSet(destination, wrapDraft(draft));
    await safeDel(source);
  });
}

function isLegacyKey(key: string): boolean {
  return key.startsWith(DRAFT_PREFIX) || key.startsWith(FILE_PREFIX);
}

export async function listLegacyWorkspaceDrafts(): Promise<
  Array<{ key: string; entryId: string; savedAt: string | null }>
> {
  return serialize(async () => {
    const metadata = [];
    for (const key of (await safeKeys()).filter(isLegacyKey)) {
      const raw = await safeGet<unknown>(key);
      metadata.push({
        key,
        entryId: key.startsWith(FILE_PREFIX)
          ? key.slice(FILE_PREFIX.length)
          : key.slice(DRAFT_PREFIX.length).replace(/^(entry|draft):/, ''),
        savedAt: unwrapDraft(raw).savedAt,
      });
    }
    return metadata;
  });
}

export async function resolveLegacyWorkspaceDrafts(
  action: 'restore' | 'discard',
  owner: string,
): Promise<void> {
  const context = captureOwner(owner);
  await serialize(async () => {
    if (!context.valid()) return;
    const legacyKeys = (await safeKeys()).filter(isLegacyKey);
    if (!context.valid()) return;
    // Quarantine the complete discard request before the first delete can fail.
    if (action === 'discard') legacyKeys.forEach(markPendingDeletion);
    for (const key of legacyKeys) {
      if (!context.valid()) return;
      if (action === 'restore') {
        const destination = ownedKey(owner, key);
        const raw = await safeGet<unknown>(key);
        const existing = await safeGet<unknown>(destination);
        if (!context.valid()) return;
        if (existing === undefined && raw !== undefined) {
          const value = key.startsWith(DRAFT_PREFIX) ? unwrapDraft(raw) : raw;
          const durable = await safeSet(destination, value);
          if (!context.valid()) {
            await safeDel(destination, true);
            return;
          }
          if (!durable) {
            // Keep the legacy copy: a memory-only adoption would be the last copy after a reload.
            memoryFallback.delete(destination);
            markWorkspaceDraftDurability(destination, true);
            throw new Error(`Legacy workspace record ${key} could not be adopted durably`);
          }
        }
      }
      if (!context.valid()) return;
      await safeDel(key, true);
    }
  });
}

export async function purgeOtherWorkspaceOwners(owner: string | null): Promise<void> {
  // Purge shares the operation queue with writes, migrations and adoption: an
  // already-running write finishes before deletion; stale queued writes abort.
  await serialize(async () => {
    const targets = (await safeKeys()).filter((key) => {
      if (pendingDeletions.has(key)) return true;
      if (!key.startsWith(OWNED_PREFIX)) return false;
      let storedOwner: unknown;
      try {
        [storedOwner] = JSON.parse(key.slice(OWNED_PREFIX.length));
      } catch {
        // Corrupt owned keys are not legacy data and should not survive purge.
      }
      return owner === null || !sameSessionUser(storedOwner, owner);
    });
    targets.forEach(markPendingDeletion);
    for (const key of targets) await safeDel(key);
  });
}

export async function __resetEntryWorkspaceStoreForTests(): Promise<void> {
  setWorkspaceOwner(null);
  await operations;
  for (const key of [...memoryFallback.keys(), ...pendingDeletions])
    markWorkspaceDraftDurability(key, true);
  pendingDeletions.clear();
  memoryFallback.clear();
}
